import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { Accordion } from 'react-bootstrap';
import {
  AppCard,
  AppDatePicker,
  AppInput,
  AppNoInfo,
  AppRemixIcon,
  AppSingleSelect,
} from '@/layout/AppComponents';
import { Scrollbars } from 'react-custom-scrollbars';
import ReactDOMServer from 'react-dom/server';
import { Formik } from 'formik';
import styled from 'styled-components';
import BreadcrumbHandler from '../../../layout/components/breadcrumb/BreadcrumbHandler';
import useIsMounted from '../../../layout/components/hooks/useIsMounted';
import * as moduleAction from '../../../store/actions/moduleAction';
import errorHandler from '@/layout/components/error/ErrorHandler';
import { axiosPath, path } from '../../../routing/helpers/router';
import AppLink from '../../../routing/helpers/AppLink';
import Translator from '../../../layout/components/helpers/Translator';
import Sidebar from '../../../layout/Sidebar/Sidebar';
import colors from '../../../constants/colors';
import NoInfo from '../../../layout/components/helpers/NoInfo';
import AppSpinner from '../../../layout/components/AppSpinner';
import minions from '../../../minions/minions';
import _ from 'lodash';

export default function LegalComplianceEvidencesScreen() {
  const { clientID, moduleBridgeHex, plantHex } = useParams();
  const dispatch = useDispatch();
  const isMounted = useIsMounted();
  const navigate = useNavigate();
  const client = useSelector((state) => state.auth.user.client);
  const [evidences, setEvidences] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedEvidence, setSelectedEvidence] = useState();
  const [initialValues] = useState({
    name: '',
    description: '',
    dateStampFrom: null,
    dateStampTo: null,
    evidenceTo: null,
    hasFile: 2,
    showAll: 0,
    obligationsHEX: '',
  });

  // < STORE CALLS
  const loadModulePermissions = useCallback(async () => {
    try {
      await dispatch(
        moduleAction.fetch(
          axiosPath(
            'api_r_user_module_legalCompliance_evaluation_getPermissions'
          ),
          'permissions',
          {
            moduleBridgeHex,
            plantHex,
            subType: 'permissions',
          }
        )
      );
      await dispatch(
        moduleAction.fetch(
          axiosPath(
            'api_r_user_module_legalCompliance_evaluation_getPermissions'
          ),
          'userBridge',
          {
            moduleBridgeHex,
            plantHex,
            subType: 'userBridge',
          }
        )
      );
      await dispatch(
        moduleAction.fetch(
          axiosPath(
            'api_r_user_module_legalCompliance_evaluation_getPermissions'
          ),
          'jsonSettings',
          {
            moduleBridgeHex,
            plantHex,
            subType: 'jsonSettings',
          }
        )
      );
    } catch (err) {}
  }, [moduleBridgeHex, plantHex]);

  useEffect(() => {
    loadModulePermissions();
  }, [moduleBridgeHex, plantHex]);
  // < STORE CALLS

  // > LOAD INTERNAL OBLIGATIONS
  const loadEvidences = useCallback(
    async (payload) => {
      setIsLoading(true);

      return axios
        .post(axiosPath('api_r_user_module_legalCompliance_evidences_list'), {
          moduleBridgeHex,
          plantHex,
          payload,
        })
        .then(({ data }) => {
          if (isMounted.current) setEvidences(data);
        })
        .catch((e) => {
          dispatch(errorHandler(e));
          navigate(
            path({
              name: 'user_module_legalCompliance_evaluation',
              params: {
                moduleBridgeHex,
                plantHex,
                page: 1,
                filtersHex: '0',
              },
            })
          );
        })
        .finally(() => setIsLoading(false));
    },
    [moduleBridgeHex, plantHex]
  );

  useEffect(() => {
    loadEvidences(initialValues);
  }, []);
  // < LOAD INTERNAL OBLIGATIONS

  const handleDownloadFile = async (url = null) => {
    try {
      const response = await axios.post(
        axiosPath('api_r_user_module_legalCompliance_evidences_download'),
        {
          evidenceID: selectedEvidence.id,
          plantHex,
          url,
        }
      );
      window.open(response.data, '_blank');
      return response.data;
    } catch (err) {}
  };

  return (
    <>
      <BreadcrumbHandler
        paths={[
          {
            name: ReactDOMServer.renderToString(
              <Translator file="module-legalCompliance" name="evidences" />
            ),
          },
        ]}
      />
      <Sidebar>
        <img
          src={client?.logoUrl}
          className="img-fluid"
          alt={client?.name}
          title={client?.name}
        />
        <Accordion>
          <ul>
            <li>
              <AppLink
                path="user_module_legalCompliance_evaluation"
                params={{
                  clientID,
                  moduleBridgeHex,
                  plantHex,
                  page: 1,
                  filtersHex: 0,
                }}
                className="sidebar-element"
              >
                <AppRemixIcon
                  icon="arrow-go-back-line"
                  className="sidebar-icon"
                  noVariants
                />{' '}
                <span className="sb-title">
                  <Translator file="module-legalCompliance" name="go.back" />
                </span>
              </AppLink>
            </li>
          </ul>
        </Accordion>
      </Sidebar>
      <main role="main" className="col-md-12 col-lg-12 px-md-4">
        <div className="justify-content-between flex-wrap flex-md-nowrap align-items-center pt-0 pb-0 mb-0 border-bottom">
          <section className="content">
            <AppCard
              header={ReactDOMServer.renderToString(
                <Translator
                  file="module-legalCompliance"
                  name="module.lc.filters"
                />
              )}
              className="mb-3"
            >
              <Formik initialValues={initialValues} onSubmit={loadEvidences}>
                {({
                  setFieldValue,
                  handleSubmit,
                  values,
                  isSubmitting,
                  errors,
                }) => (
                  <form onSubmit={handleSubmit}>
                    <div className="row">
                      <div className="col-6">
                        <AppInput
                          title={ReactDOMServer.renderToString(
                            <Translator
                              file="module-legalCompliance"
                              name="title"
                            />
                          )}
                          placeholder={ReactDOMServer.renderToString(
                            <Translator
                              file="module-legalCompliance"
                              name="module.lc.search-title"
                            />
                          )}
                          icon={<AppRemixIcon icon="font-color" noVariants />}
                          onChange={(value) => setFieldValue('name', value)}
                          value={values.name}
                          isClearable
                          error={errors.name}
                        />
                        <AppInput
                          title={ReactDOMServer.renderToString(
                            <Translator
                              file="module-legalCompliance"
                              name="module.lc.evidence.description"
                            />
                          )}
                          placeholder={ReactDOMServer.renderToString(
                            <Translator
                              file="module-legalCompliance"
                              name="module.lc.search-description"
                            />
                          )}
                          icon={<AppRemixIcon icon="keyboard" />}
                          onChange={(value) =>
                            setFieldValue('description', value)
                          }
                          value={values.description}
                          isClearable
                          error={errors.description}
                        />
                        <AppDatePicker
                          title={ReactDOMServer.renderToString(
                            <Translator
                              file="module-legalCompliance"
                              name="module.lc.from"
                            />
                          )}
                          value={values?.dateStampFrom}
                          placeholder="DD/MM/YYYY"
                          onChange={(opt) =>
                            setFieldValue('dateStampFrom', opt)
                          }
                          popperClassName="react-datepicker-popper react-datepicker-popper-1000"
                          isClearable
                          showYearDropdown
                          showMonthDropdown
                          dateFormatCalendar="MMMM"
                          hasToggleType
                          calendarStartDay={1}
                        />
                        <AppDatePicker
                          title={ReactDOMServer.renderToString(
                            <Translator
                              file="module-legalCompliance"
                              name="module.lc.to"
                            />
                          )}
                          value={values?.dateStampTo}
                          placeholder="DD/MM/YYYY"
                          onChange={(opt) => setFieldValue('dateStampTo', opt)}
                          popperClassName="react-datepicker-popper react-datepicker-popper-1000"
                          isClearable
                          showYearDropdown
                          showMonthDropdown
                          dateFormatCalendar="MMMM"
                          hasToggleType
                          calendarStartDay={1}
                        />
                        <AppSingleSelect
                          icon={<AppRemixIcon icon="survey" />}
                          options={[
                            {
                              value: 0,
                              label: ReactDOMServer.renderToString(
                                <Translator
                                  file="module-legalCompliance"
                                  name="module.lc.evidence-without-file"
                                />
                              ),
                            },
                            {
                              value: 1,
                              label: ReactDOMServer.renderToString(
                                <Translator
                                  file="module-legalCompliance"
                                  name="module.lc.evidence-with-file"
                                />
                              ),
                            },
                            {
                              value: 2,
                              label: ReactDOMServer.renderToString(
                                <Translator
                                  file="module-legalCompliance"
                                  name="module.lc.view-all-evidence"
                                />
                              ),
                            },
                          ]}
                          title={ReactDOMServer.renderToString(
                            <Translator
                              file="module-legalCompliance"
                              name="module.lc.files"
                            />
                          )}
                          value={values.hasFile}
                          onChange={(opt) => setFieldValue('hasFile', opt)}
                        />

                        <AppSingleSelect
                          icon={<AppRemixIcon icon="survey" />}
                          options={[
                            {
                              value: 0,
                              label: ReactDOMServer.renderToString(
                                <Translator
                                  file="module-legalCompliance"
                                  name="module.lc.show-only-latest-evidence"
                                />
                              ),
                            },
                            {
                              value: 1,
                              label: ReactDOMServer.renderToString(
                                <Translator
                                  file="module-legalCompliance"
                                  name="module.lc.show-all-evidence"
                                />
                              ),
                            },
                          ]}
                          title={ReactDOMServer.renderToString(
                            <Translator
                              file="module-legalCompliance"
                              name="module.lc.evidence-presented"
                            />
                          )}
                          value={values.showAll}
                          onChange={(opt) => setFieldValue('showAll', opt)}
                        />

                        <AppInput
                          icon={<AppRemixIcon icon="fingerprint" />}
                          title={ReactDOMServer.renderToString(
                            <Translator
                              file="module-legalCompliance"
                              name="module.lc.export.pdf1"
                            />
                          )}
                          placeholder={ReactDOMServer.renderToString(
                            <Translator
                              file="module-legalCompliance"
                              name="module.lc.code-obligations-separated-commas"
                            />
                          )}
                          value={values?.obligationsHEX ?? ''}
                          onChange={(value) =>
                            setFieldValue(
                              'obligationsHEX',
                              value.replace(/[^0-9a-fA-F,]/, '')
                            )
                          }
                          isClearable
                        />

                        <button
                          type="submit"
                          className="btn btn-xs btn-outline-light"
                          disabled={isSubmitting}
                          icon="save-3"
                        >
                          <Translator
                            file="module-legalCompliance"
                            name="filtering"
                          />
                        </button>
                      </div>
                    </div>
                  </form>
                )}
              </Formik>
            </AppCard>

            <AppCard
              header={ReactDOMServer.renderToString(
                <Translator file="module-legalCompliance" name="evidences" />
              )}
            >
              {isLoading ? (
                <NoInfo
                  text={<Translator file="general" name="loading" />}
                  icon={
                    <AppSpinner>
                      <AppRemixIcon icon="loader-4" size={24} color="#ccc" />
                    </AppSpinner>
                  }
                />
              ) : (
                <div>
                  {Object.keys(evidences).length > 0 ? (
                    <EvidenceTabContainer>
                      <SidebarMenu>
                        <>
                          <Scrollbars style={{ height: '100%' }}>
                            <ul>
                              {_.map(evidences, (obligation, obligationKey) =>
                                _.map(obligation, (evidence, key) => (
                                  <li
                                    key={key}
                                    className={
                                      selectedEvidence &&
                                      selectedEvidence.id === evidence.id
                                        ? 'ev-selected'
                                        : ''
                                    }
                                    onClick={() => {
                                      setSelectedEvidence(evidence);
                                    }}
                                  >
                                    <div className="first-row">
                                      <span
                                        className="name"
                                        title={
                                          evidence.name || evidence.description
                                        }
                                      >
                                        {evidence.name || evidence.description}
                                      </span>
                                      <AppRemixIcon
                                        className="mr-2"
                                        icon="attachment-2"
                                        noVariants
                                        color={
                                          evidence.url ||
                                          evidence?.urls?.length > 0
                                            ? colors.primary
                                            : colors.grey400
                                        }
                                      />
                                    </div>
                                    <div className="second-row">
                                      <span className="sr-date">
                                        <div>{evidence.date}</div>
                                        <div>
                                          ID:{evidence.obligation.id} - CODE:{' '}
                                          {obligationKey}
                                        </div>
                                      </span>
                                      <span className="sr-user mr-2">
                                        {evidence.user}
                                      </span>
                                    </div>
                                  </li>
                                ))
                              )}
                            </ul>
                          </Scrollbars>
                        </>
                      </SidebarMenu>
                      <Content>
                        <>
                          {selectedEvidence ? (
                            <>
                              <h3>
                                <Translator
                                  file="module-legalCompliance"
                                  name="evidence.information.name"
                                  variables={{
                                    name: selectedEvidence.name,
                                  }}
                                />
                                <span
                                  className="float-right pointer mr-2"
                                  title={ReactDOMServer.renderToString(
                                    <Translator
                                      file="module-legalCompliance"
                                      name="close"
                                    />
                                  )}
                                >
                                  <AppRemixIcon
                                    icon="close"
                                    size={16}
                                    onClick={() => {
                                      setSelectedEvidence(null);
                                    }}
                                  />
                                </span>
                              </h3>
                              <EvidenceInfo className="mt-4">
                                <li>
                                  <span className="eventinfo-title">
                                    <Translator
                                      file="module-legalCompliance"
                                      name="name"
                                    />
                                  </span>
                                  <span className="eventinfo-content">
                                    {selectedEvidence.name}
                                  </span>
                                </li>
                                <li>
                                  <span className="eventinfo-title">CODE</span>
                                  <span className="eventinfo-content">
                                    {selectedEvidence.obligation?.hex}
                                  </span>
                                </li>
                                <li>
                                  <span className="eventinfo-title">ID</span>
                                  <span className="eventinfo-content">
                                    {selectedEvidence.obligation?.id}
                                  </span>
                                </li>
                                <li>
                                  <span className="eventinfo-title">
                                    <Translator
                                      file="module-legalCompliance"
                                      name="associated.files"
                                    />
                                  </span>
                                  <span className="eventinfo-content">
                                    {selectedEvidence.urls &&
                                    selectedEvidence.urls.length > 0 ? (
                                      selectedEvidence.urls.map((url) => (
                                        <FileDownload
                                          key={url}
                                          onClick={() =>
                                            handleDownloadFile(url)
                                          }
                                          title={ReactDOMServer.renderToString(
                                            <Translator
                                              file="module-legalCompliance"
                                              name="download.file"
                                            />
                                          )}
                                        >
                                          {minions.files.fileIcon(
                                            url,
                                            true,
                                            null,
                                            'mr-2'
                                          )}
                                          {url}
                                        </FileDownload>
                                      ))
                                    ) : (
                                      <span>
                                        <Translator
                                          file="module-legalCompliance"
                                          name="no.associated.files"
                                        />
                                      </span>
                                    )}
                                  </span>
                                </li>
                                <li>
                                  <span className="eventinfo-title">
                                    <Translator
                                      file="user"
                                      name="user.obligation.evidence.date"
                                    />
                                  </span>
                                  <span className="eventinfo-content">
                                    {selectedEvidence.date ? (
                                      selectedEvidence.date
                                    ) : (
                                      <span className="text-muted">
                                        <Translator
                                          file="user"
                                          name="user.no-date"
                                        />
                                      </span>
                                    )}
                                  </span>
                                </li>
                                <li>
                                  <span className="eventinfo-title">
                                    <Translator
                                      file="module-legalCompliance"
                                      name="module.lc.evidence.description"
                                    />
                                  </span>
                                  <span
                                    className="eventinfo-content"
                                    style={{
                                      maxWidth: 'calc(100vw - 900px)',
                                    }}
                                  >
                                    {selectedEvidence.description}
                                  </span>
                                </li>
                                <li>
                                  <span className="eventinfo-title">
                                    <Translator
                                      file="module-intelligence"
                                      name="obligation"
                                    />
                                  </span>
                                  <span
                                    className="eventinfo-content"
                                    style={{ maxWidth: '80%' }}
                                  >
                                    {selectedEvidence.obligation.text}
                                  </span>
                                </li>
                              </EvidenceInfo>
                            </>
                          ) : (
                            <></>
                          )}
                        </>
                      </Content>
                    </EvidenceTabContainer>
                  ) : (
                    <AppNoInfo
                      text={
                        <Translator
                          file="module-legalCompliance"
                          name="no.evidences"
                        />
                      }
                      color={colors.primaryLight}
                      icon={
                        <AppRemixIcon
                          size={24}
                          icon="file-search"
                          color={colors.primaryLight}
                        />
                      }
                    />
                  )}
                </div>
              )}
            </AppCard>
          </section>
        </div>
      </main>
    </>
  );
}

const EvidenceTabContainer = styled.div`
  height: 400px;
  display: grid;
  grid-template-columns: 450px 1fr;
`;

const SidebarMenu = styled.div`
  overflow-y: auto;
  padding: 0;

  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  ul {
    padding: 4px;
    overflow-y: auto;
    li {
      padding: 4px 2px;
      border-bottom: 1px solid ${colors.grey300};
      cursor: pointer;
      &.ev-selected {
        background: ${colors.grey300};
      }
      .first-row {
        display: flex;
        justify-content: space-between;
        align-items: center;
        color: ${colors.primary};
        .name {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
          text-overflow: ellipsis;
        }
      }
      .second-row {
        display: flex;
        justify-content: space-between;
        align-items: center;
        font-size: 10px;
        .sr-date {
          letter-spacing: 1px;
        }
        .sr-user {
          color: ${colors.primary};
        }
      }
    }
  }
`;

const Content = styled.div`
  border-left: 1px solid ${colors.grey300};
  padding: 4px;
  height: 400px;
  .row-no-margin > .row {
    margin: 0;
  }
`;

const EvidenceInfo = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  height: 350px;
  overflow: auto;
  li {
    padding: 8px 0;
    border-top: 1px solid ${colors.grey300};
    display: flex;
    align-items: baseline;
    .eventinfo-title {
      display: inline-block;
      width: 200px;
      text-transform: uppercase;
      font-weight: bold;
      color: ${colors.primary};
    }
  }
`;

const FileDownload = styled.div`
  &:hover {
    cursor: pointer;
  }
`;
